import type { LayoutInput, LayoutResult, SortOptions } from '../types';
import { SORT_DEFAULTS } from '../defaults';
import { shapeBounds } from '../utils/geometry';
import { applyAnchor } from '../utils/anchor';

interface SortRect {
	id: string;
	width: number;
	height: number;
}

interface PlacedRect {
	id: string;
	x: number;
	y: number;
	width: number;
	height: number;
}

/**
 * Column masonry: fixed-width columns, each node drops into the shortest
 * column. Nodes are centered horizontally within their column.
 */
function masonrySort(
	rects: SortRect[],
	numCols: number,
	colWidth: number,
	spacing: number,
): PlacedRect[] {
	const placed: PlacedRect[] = [];
	const colHeights = new Array(numCols).fill(0);

	for (const rect of rects) {
		let bestCol = 0;
		for (let c = 1; c < numCols; c++) {
			if (colHeights[c] < colHeights[bestCol]) bestCol = c;
		}

		const x = bestCol * (colWidth + spacing);
		const y = colHeights[bestCol];
		const xOffset = (colWidth - rect.width) / 2;

		placed.push({ id: rect.id, x: x + xOffset, y, width: rect.width, height: rect.height });
		colHeights[bestCol] = y + rect.height + spacing;
	}

	return placed;
}

function placementBBox(placed: PlacedRect[]): { width: number; height: number } {
	if (placed.length === 0) return { width: 0, height: 0 };
	let maxX = 0;
	let maxY = 0;
	for (const p of placed) {
		if (p.x + p.width > maxX) maxX = p.x + p.width;
		if (p.y + p.height > maxY) maxY = p.y + p.height;
	}
	return { width: maxX, height: maxY };
}

/**
 * Sort nodes into a compact column-based masonry layout (Pinterest-style).
 *
 * Nodes drop into the shortest column, producing uniform vertical gaps. Tries
 * multiple column counts and picks the most compact square-ish result.
 */
export function sort(
	input: LayoutInput,
	options?: Partial<SortOptions>,
): LayoutResult {
	const opts: SortOptions = { ...SORT_DEFAULTS, ...options };
	const { nodes } = input;

	if (nodes.length === 0) return { nodes: [] };
	if (nodes.length === 1) {
		const bounds = shapeBounds(nodes[0].shape);
		return { nodes: [{ id: nodes[0].id, x: bounds.x, y: bounds.y }] };
	}

	const rects: SortRect[] = nodes.map((n) => {
		const b = shapeBounds(n.shape);
		return { id: n.id, width: b.width, height: b.height };
	});

	rects.sort((a, b) => {
		const dh = b.height - a.height;
		if (Math.abs(dh) > 0.01) return dh;
		return b.width * b.height - a.width * a.height;
	});

	let colWidth = 0;
	for (const r of rects) {
		if (r.width > colWidth) colWidth = r.width;
	}

	const maxCols = Math.min(rects.length, Math.max(6, Math.ceil(Math.sqrt(rects.length * 3))));
	let bestPlaced: PlacedRect[] | null = null;
	let bestScore = Infinity;

	for (let cols = 1; cols <= maxCols; cols++) {
		const placed = masonrySort(rects, cols, colWidth, opts.spacing);
		const bbox = placementBBox(placed);
		const score = Math.max(bbox.width, bbox.height);
		if (score < bestScore) {
			bestScore = score;
			bestPlaced = placed;
		}
	}

	const anchored = applyAnchor(
		nodes,
		bestPlaced!.map((p) => ({ id: p.id, x: p.x, y: p.y })),
		opts.anchor,
	);

	return { nodes: anchored.map((a) => ({ id: a.id, x: a.x, y: a.y })) };
}
